import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { currencyFormatter, composeEventName } from 'toolkit/utils';
import useCurrency from 'toolkit/useHooks/useCurrency';

export const useCategoryLimitValidation = () => {
  const { t } = useTranslation('translations', { useSuspense: false });
  const { user } = useSelector((state) => state.auth);
  const betSlip = useSelector((state) => state.betSlip);
  const { code: currencyDisplayCode } = useCurrency();
  const _currencyFormatter = currencyFormatter(currencyDisplayCode, 2);
  const customerLimits = user.customerLimitDto;
  const limits = customerLimits;

  const customerOrProductLimits = {
    A: limits.categoryLimitA,
    B: limits.categoryLimitB,
    C: limits.categoryLimitC,
    D: limits.categoryLimitD,
    E: limits.categoryLimitE
  };

  const checkLimitForEvent = (totalStake, event) => {
    const category = event.limitCategory;
    const customerOrProductLimit = customerOrProductLimits[category];

    const hasLimit = totalStake <= customerOrProductLimit;

    if (!hasLimit) {
      return {
        text: t('categoryLimit', {
          game: composeEventName(event, t),
          limit: _currencyFormatter.format(customerOrProductLimit)
        }),
        type: 'error',
        valid: false
      };
    }
    return {
      text: '',
      type: 'none',
      valid: true
    };
  };

  const validateBetSlipCategoryLimit = (betSlipEvents, stake) => {
    if (!limits) {
      return {
        text: t('noLimits'),
        type: 'warning',
        valid: false
      };
    }
    const activeOutcomes = betSlip.bets
      .filter((o) => o.enabled)
      .map((b) => {
        return {
          ...b,
          match: b,
          enabled: b.enabled
        };
      });

    for (const outcome of activeOutcomes) {
      const event = betSlipEvents[outcome.providerId];
      if (!event) {
        continue;
      }
      const limitValidation = checkLimitForEvent(stake, event);
      if (!limitValidation.valid) {
        return limitValidation;
      }
    }

    if (!betSlip.system) {
      const categories = ['A', 'B', 'C', 'D', 'E'];
      const highestCategory = categories.find((category) =>
        betSlip.bets.some((outcome) => outcome.match.limitCategory === category)
      );

      if (highestCategory && limits) {
        const minBet = (function () {
          switch (highestCategory) {
            case 'A':
              return limits.categoryAMinBet;
            case 'B':
              return limits.categoryBMinBet;
            case 'C':
              return limits.categoryCMinBet;
            case 'D':
              return limits.categoryDMinBet;
            case 'E':
              return limits.categoryEMinBet;
            default:
              return 0;
          }
        })();

        if (Object.values(betSlip.bets).length < minBet) {
          return {
            text: t('categoryMinBet', {
              minBet: minBet
            }),
            type: 'error',
            valid: false
          };
        }
      }
    }

    return {
      text: '',
      type: 'none',
      valid: true
    };
  };

  return { validateBetSlipCategoryLimit };
};
